import { z } from "zod";

import { commentProviderIdSchema } from "./discussion";
import {
  contentStatusSchema,
  contentTypeSchema,
  entityIdSchema,
  gitShaSchema,
  httpsUrlSchema,
  isoDateSchema,
  isoInstantSchema,
  localeSchema,
  repoPathSchema,
  slugSchema,
} from "./primitives";

/**
 * ContentEntry, ContentLocalization, Draft and the Markdown frontmatter contract.
 *
 * The central idea (AGENTS.md §8): a logical article is a `ContentEntry` with
 * one `ContentLocalization` per language. There is deliberately no
 * `Article.language` field anywhere.
 */

// ---------------------------------------------------------------------------
// SEO
// ---------------------------------------------------------------------------

/** Per-localization SEO overrides (SPEC §9.1, §49). */
export const seoSchema = z
  .object({
    title: z.string().max(200).optional(),
    description: z.string().max(500).optional(),
    canonical: httpsUrlSchema.optional(),
    /** `og:image` / `twitter:image`. */
    image: httpsUrlSchema.optional(),
    keywords: z.array(z.string().min(1).max(60)).max(30).optional(),
    noindex: z.boolean().optional(),
  })
  .strict();

export type SEOConfig = z.infer<typeof seoSchema>;

// ---------------------------------------------------------------------------
// Frontmatter
// ---------------------------------------------------------------------------

/**
 * A reference to a media asset.
 *
 * Either an absolute public URL (what the editor inserts, SPEC §14) or a
 * site-root-relative path such as `/assets/cloudflare-cms.webp` (SPEC §12).
 */
export const mediaRefSchema = z.union([
  httpsUrlSchema,
  z
    .string()
    .regex(/^\/[^\s?#]+$/, "Must be an absolute URL or a site-root-relative path")
    .refine((value) => !value.includes(".."), { message: "Must not contain '..'" }),
]);

export type MediaRef = z.infer<typeof mediaRefSchema>;

/**
 * Canonical Markdown frontmatter (SPEC §12).
 *
 * `updated` and `description` are optional; everything else is required, because
 * a published file without a title, slug, date or status cannot be rendered or
 * routed. Unknown keys are preserved (`passthrough`) so that a hand-edited
 * content repository does not explode the sync job.
 */
export const frontmatterSchema = z
  .object({
    title: z.string().min(1, "Frontmatter requires a title").max(300),
    slug: slugSchema,
    description: z.string().max(500).optional(),
    date: isoDateSchema,
    updated: isoDateSchema.optional(),
    status: contentStatusSchema,
    /** Category slug. Resolved against the site's category list. */
    category: slugSchema.optional(),
    tags: z.array(slugSchema).max(50).default([]),
    cover: mediaRefSchema.optional(),
    seo: seoSchema.optional(),
  })
  .passthrough();

export type Frontmatter = z.infer<typeof frontmatterSchema>;
export type FrontmatterInput = z.input<typeof frontmatterSchema>;

/**
 * Frontmatter while a draft is still being written.
 *
 * Every field is optional and `slug` is an unvalidated string: a user typing a
 * title must not be blocked by a half-finished slug. Canonical validation
 * happens at publish time (TASK-503) — the server, not the browser, decides
 * (AGENTS.md §10).
 */
export const draftFrontmatterSchema = z
  .object({
    title: z.string().max(300).optional(),
    slug: z.string().max(200).optional(),
    description: z.string().max(500).optional(),
    date: isoDateSchema.optional(),
    updated: isoDateSchema.optional(),
    status: contentStatusSchema.optional(),
    category: z.string().max(200).optional(),
    tags: z.array(z.string().max(200)).max(50).optional(),
    cover: z.string().max(2000).optional(),
    seo: seoSchema.optional(),
  })
  .passthrough();

export type DraftFrontmatter = z.infer<typeof draftFrontmatterSchema>;

// ---------------------------------------------------------------------------
// ContentEntry
// ---------------------------------------------------------------------------

/** A logical piece of content, independent of language (SPEC §8). */
export const contentEntrySchema = z
  .object({
    id: entityIdSchema,
    siteId: entityIdSchema,
    type: contentTypeSchema,
    canonicalLocale: localeSchema,

    /** Directory inside the content repository, e.g. `posts/cloudflare-workers-cms`. */
    contentPath: repoPathSchema,

    githubSha: gitShaSchema.optional(),

    createdAt: isoInstantSchema,
    updatedAt: isoInstantSchema,
  })
  .strict();

export type ContentEntry = z.infer<typeof contentEntrySchema>;

/**
 * The comment thread mapped to one published localization (SPEC §30).
 *
 * Recorded in `entry.json` at publish time so the static build can embed the
 * right thread without ever talking to D1 or the CMS API (AGENTS.md §4).
 */
export const entryDiscussionSchema = z
  .object({
    provider: commentProviderIdSchema,
    /** The provider's human-facing thread number, for building embed URLs. */
    number: z.number().int().positive(),
  })
  .strict();

export type EntryDiscussion = z.infer<typeof entryDiscussionSchema>;

/**
 * `entry.json` — the on-disk entry descriptor (SPEC §8.3).
 *
 * `locales` lists which localizations actually exist on disk. `id` is stable and
 * must never be derived from slug or locale, so renaming a URL keeps the entry
 * (and its Discussions, and its history) intact.
 */
export const entryJsonSchema = z
  .object({
    id: entityIdSchema,
    type: contentTypeSchema,
    canonicalLocale: localeSchema,
    locales: z.array(localeSchema).min(1),
    /**
     * Published comment-thread mappings, keyed by locale (TASK-702/703).
     * Written by the publish pipeline; a plain Git edit may omit it.
     */
    discussions: z.record(localeSchema, entryDiscussionSchema).optional(),
    createdAt: isoInstantSchema,
    updatedAt: isoInstantSchema,
  })
  .strict()
  .refine((entry) => entry.locales.includes(entry.canonicalLocale), {
    message: "canonicalLocale must be one of locales",
    path: ["canonicalLocale"],
  })
  .refine((entry) => new Set(entry.locales).size === entry.locales.length, {
    message: "locales must not contain duplicates",
    path: ["locales"],
  })
  .refine(
    (entry) =>
      entry.discussions === undefined ||
      Object.keys(entry.discussions).every((locale) => entry.locales.includes(locale)),
    {
      message: "discussions must be keyed by a listed locale",
      path: ["discussions"],
    },
  );

export type EntryJson = z.infer<typeof entryJsonSchema>;

// ---------------------------------------------------------------------------
// ContentLocalization
// ---------------------------------------------------------------------------

/**
 * One language version of an entry (SPEC §9.1).
 *
 * `siteId` is carried here even though the SPEC interface omits it: the
 * database uniqueness constraint is `(site_id, locale, slug)` (SPEC §55) and
 * every site-scoped query must filter on it (AGENTS.md §19).
 */
export const contentLocalizationSchema = z
  .object({
    entryId: entityIdSchema,
    siteId: entityIdSchema,
    locale: localeSchema,

    title: z.string().min(1).max(300),
    slug: slugSchema,
    description: z.string().max(500).optional(),

    status: contentStatusSchema,

    publishedAt: isoInstantSchema.optional(),
    /** Set when `status` is `scheduled` (SPEC §28). */
    scheduledAt: isoInstantSchema.optional(),
    updatedAt: isoInstantSchema,

    /** Path of the Markdown file inside the content repository. */
    contentPath: repoPathSchema,
    githubSha: gitShaSchema.optional(),

    seo: seoSchema.optional(),
  })
  .strict()
  .refine((loc) => loc.status !== "scheduled" || loc.scheduledAt !== undefined, {
    message: "A scheduled localization requires scheduledAt",
    path: ["scheduledAt"],
  })
  .refine((loc) => loc.status !== "published" || loc.publishedAt !== undefined, {
    message: "A published localization requires publishedAt",
    path: ["publishedAt"],
  });

export type ContentLocalization = z.infer<typeof contentLocalizationSchema>;

/** An entry together with all of its localizations — the Admin-facing shape. */
export const contentEntryWithLocalizationsSchema = contentEntrySchema.extend({
  localizations: z.array(contentLocalizationSchema),
});

export type ContentEntryWithLocalizations = z.infer<typeof contentEntryWithLocalizationsSchema>;

// ---------------------------------------------------------------------------
// Draft
// ---------------------------------------------------------------------------

/**
 * A working copy held in D1 only (SPEC §27).
 *
 * A draft is not a Git file. Autosave writes here and never produces a commit
 * (AGENTS.md §16) — only an explicit publish does.
 */
export const draftSchema = z
  .object({
    id: entityIdSchema,

    siteId: entityIdSchema,
    entryId: entityIdSchema,
    locale: localeSchema,

    /** Markdown body, without frontmatter. */
    content: z.string().max(2_000_000),
    frontmatter: draftFrontmatterSchema,

    /**
     * SHA of the commit the draft was based on. Publishing compares this with
     * the repository's current SHA and refuses to overwrite external changes
     * (SPEC §25, AGENTS.md §17).
     */
    baseGithubSha: gitShaSchema.optional(),

    createdAt: isoInstantSchema,
    updatedAt: isoInstantSchema,
  })
  .strict();

export type Draft = z.infer<typeof draftSchema>;

/** Body of `PUT /api/sites/:siteId/entries/:entryId/:locale/draft` (SPEC §56). */
export const draftSaveSchema = z
  .object({
    content: z.string().max(2_000_000),
    frontmatter: draftFrontmatterSchema,
    baseGithubSha: gitShaSchema.optional(),
  })
  .strict();

export type DraftSave = z.infer<typeof draftSaveSchema>;

/**
 * Translate a validated draft into canonical frontmatter.
 *
 * Throws a `ZodError` when the draft is not yet publishable — callers convert
 * that into a `VALIDATION_ERROR` response rather than leaking the Zod issue list.
 */
export function draftToFrontmatter(draft: Draft): Frontmatter {
  const fm = draft.frontmatter;

  return frontmatterSchema.parse({
    ...fm,
    title: fm.title ?? "",
    slug: fm.slug ?? "",
    date: fm.date ?? draft.updatedAt.slice(0, 10),
    status: fm.status ?? "draft",
    tags: fm.tags ?? [],
  });
}
